import type { ComponentProps } from "react";
import { useState } from "react";
import { DealOutGrid, EmptyStatePage, GlassCard, Glyph } from "@/components/application/empty-state-page";
import { ProductSelectionModal } from "./components/ProductSelectionModal";

type TheaterProps = ComponentProps<typeof ProductSelectionModal>["theaterProps"];

const CARDS: { icon: string; title: string; tags: string[] }[] = [
  {
    icon: "M12 17a5 5 0 1 0 10 0 5 5 0 0 0-10 0Zm0 0c0-1.126.372-2.165 1-3V5m-1 12c0 .825.2 1.604.554 2.29-.842.712-2.788 1.21-5.054 1.21-3.038 0-5.5-.895-5.5-2V5m11 0c0 1.105-2.462 2-5.5 2S2 6.105 2 5m11 0c0-1.105-2.462-2-5.5-2S2 3.895 2 5m0 9c0 1.105 2.462 2 5.5 2 2.189 0 4.08-.465 4.965-1.138M13 9.5c0 1.105-2.462 2-5.5 2S2 10.605 2 9.5",
    title: "Sales tactics",
    tags: ["Bundle offer", "Free shipping", "Scarcity"],
  },
  {
    icon: "M12 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 0v14m0 0A10 10 0 0 1 2 12h3m7 10a10 10 0 0 0 10-10h-3",
    title: "Common hooks",
    tags: ["Surprising statistic", "Transformation"],
  },
  {
    icon: "m9 11.5 2 2L15.5 9m4.5 3c0 4.908-5.354 8.478-7.302 9.615-.221.129-.332.194-.488.227a1.137 1.137 0 0 1-.42 0c-.156-.034-.267-.098-.488-.227C9.354 20.478 4 16.908 4 12V7.217c0-.799 0-1.199.13-1.542a2 2 0 0 1 .548-.79c.276-.243.65-.383 1.398-.664l5.362-2.01c.208-.078.312-.117.419-.133a1 1 0 0 1 .286 0c.107.016.21.055.419.133l5.362 2.01c.748.28 1.123.421 1.398.664a2 2 0 0 1 .547.79c.131.343.131.743.131 1.542V12Z",
    title: "Safety guides",
    tags: ["No kids", "No alcohol or tobacco"],
  },
];

/** The visual empty state on its own, without the product-selection flow — used by the preview harness. */
export function SmartProductBriefsEmptyStateView({ onGenerate }: { onGenerate: () => void }) {
  return (
    <EmptyStatePage
      eyebrow="Smart Product Briefs"
      headline={
        <>
          Brief creators on
          <br />
          what actually sells
        </>
      }
      subline="Each brief is generated from the hooks, formats and products already converting on TikTok, with your own safety rules applied. You’re not writing it from scratch, and you’re not guessing what works."
      measureCh={46}
      portraitSrc="/empty-states/portrait-smart-product-briefs.webp"
      portraitMaskLeft="28%"
      portraitMaskBottom={null}
      portraitClassName="top-1/2 -right-16 h-[105%] w-auto -translate-y-1/2 -scale-x-100 opacity-55"
      actionsDelaySeconds={1.25}
      primaryAction={{ label: "Generate a brief", onClick: onGenerate, icon: "sparkles" }}
      device={
        <DealOutGrid count={CARDS.length} className="max-w-[720px] grid-cols-3">
          {(index, { dealt, blurDelayMs }) => {
            const card = CARDS[index];
            return (
              <GlassCard
                radius="2xl"
                className="h-full"
                style={{
                  backdropFilter: dealt ? "blur(64px)" : "blur(0px)",
                  WebkitBackdropFilter: dealt ? "blur(64px)" : "blur(0px)",
                  transition: `backdrop-filter 520ms ease-out ${blurDelayMs}ms, -webkit-backdrop-filter 520ms ease-out ${blurDelayMs}ms`,
                }}
              >
                <div className="flex flex-col items-start gap-3 px-4 py-4">
                  <span
                    aria-hidden="true"
                    className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-500/55 text-white ring-1 ring-brand-300/40 ring-inset backdrop-blur-sm"
                  >
                    <Glyph d={card.icon} />
                  </span>
                  <p className="text-base font-semibold text-white">{card.title}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {card.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-white/12 px-2.5 py-1 text-[13px] text-brand-100 ring-1 ring-white/15 ring-inset"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </GlassCard>
            );
          }}
        </DealOutGrid>
      }
    />
  );
}

export function SmartProductBriefsEmptyState({ theaterProps }: { theaterProps: TheaterProps }) {
  const [showProductModal, setShowProductModal] = useState(false);

  return (
    <>
      <SmartProductBriefsEmptyStateView onGenerate={() => setShowProductModal(true)} />
      <ProductSelectionModal
        isOpen={showProductModal}
        onClose={() => {
          setShowProductModal(false);
          theaterProps?.onPreselectionProcessed?.();
        }}
        navigateToEdit
        theaterProps={theaterProps}
        preselectedProductId={theaterProps?.preselectedProductId}
        preselectedProductName={theaterProps?.preselectedProductName}
      />
    </>
  );
}
